import React, { useState, useEffect, useRef, ReactNode } from 'react';
import Logo, { CleroS, CleroWordmarkSvg } from '../Logo';
import { AppStoreBadges, BottomAppShowcase, PageShell, InstitutionalTrustBar } from '../public/PublicPrimitives';
import { ChevronRight } from 'lucide-react';
import Lenis from 'lenis';

import { ANDROID_INVITE_URL, IOS_INVITE_URL } from '../../constants/downloadLinks';

interface CompareShellProps {
  children: ReactNode;
  currentPageTitle?: string;
  competitorName?: string;
  competitorSlug?: string;
  metaDescription?: string;
}

export { ANDROID_INVITE_URL, IOS_INVITE_URL, AppStoreBadges, InstitutionalTrustBar };

export const CompareShell: React.FC<CompareShellProps> = ({
  children,
  competitorName,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 0.85,
    });
    lenisRef.current = lenis;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };

    lenis.scrollTo(0, { immediate: true });
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    lenis.on('scroll', handleScroll);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    let rafId: number;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  const navigate = (path: string) => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    }
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  return (
    <PageShell className="overflow-x-hidden bg-[#fafaf7]">
      {/* CONTINUOUS MORPHING HEADER */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isScrolled ? 'pt-3.5 px-4 sm:px-6' : 'pt-0 px-0'
        }`}
      >
        <header
          className={`pointer-events-auto w-full transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isScrolled
              ? 'max-w-[480px] rounded-[999px] bg-white/85 backdrop-blur-2xl border border-slate-200/80 px-4 sm:px-5 py-2 shadow-sm'
              : 'max-w-[720px] sm:max-w-[760px] rounded-[0px] bg-transparent backdrop-blur-none border-b border-slate-200/50 px-4 sm:px-6 py-3.5'
          }`}
        >
          <div className="flex items-center justify-between gap-4 sm:gap-6">
            {/* Clero Logo */}
            <div className="flex items-center gap-2 shrink-0">
              <a
                aria-label="Clero Home"
                className="flex items-center transition opacity-95 hover:opacity-100"
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
              >
                <CleroWordmarkSvg height={24} color="#0D9488" />
              </a>
            </div>

            {/* Right Action Area: Help Center + Download App CTA */}
            <div className="flex items-center gap-2.5 sm:gap-4 shrink-0">
              <button
                className="text-xs sm:text-sm font-bold text-slate-700 hover:text-slate-950 transition-colors whitespace-nowrap px-1 py-1"
                onClick={() => navigate('/support')}
                type="button"
              >
                Help Center
              </button>

              <button
                className="inline-flex items-center justify-center rounded-full bg-[var(--clero-primary)] px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs font-bold text-white transition-all hover:bg-[var(--clero-primary-dark)] shadow-sm active:scale-95 whitespace-nowrap"
                onClick={() => {
                  const el = document.getElementById('bottom-download');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                type="button"
              >
                Download App
              </button>
            </div>
          </div>
        </header>
      </div>

      {/* Spacing compensation for fixed navbar */}
      <div className="h-16 sm:h-20" />

      {/* MAIN COMPARISON CONTENT */}
      <main>
        {children}
      </main>

      <div className="mt-12 mx-auto max-w-[var(--clero-max-content)] px-4 sm:px-6 lg:px-8 mb-12">
        <BottomAppShowcase />
      </div>

      {/* FOOTER */}
      <footer className="border-t border-slate-200/80 bg-white px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1140px]">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-4 sm:col-span-2 lg:col-span-1">
              <img src="/clero_icon.png" alt="Clero" className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl shadow-sm object-cover" />
              <p className="text-xs font-semibold text-slate-400">
                Secured with direct bank connections via Plaid & Astra Financial.
              </p>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Compare</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare')} type="button">
                    All Splitting Apps
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/splitwise')} type="button">
                    Clero vs Splitwise
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/venmo')} type="button">
                    Clero vs Venmo
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/zelle')} type="button">
                    Clero vs Zelle
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Product</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/')} type="button">Features</button></li>
                <li><a className="transition hover:text-slate-950" href={IOS_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for iOS</a></li>
                <li><a className="transition hover:text-slate-950" href={ANDROID_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for Android</a></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Resources</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/blog')} type="button">Blog</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/support')} type="button">Help & Support</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/compare')} type="button">Compare Apps</button></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Legal & Privacy</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/privacy')} type="button">Privacy Policy</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/terms')} type="button">Terms of Service</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/data-deletion')} type="button">Data Deletion</button></li>
              </ul>
            </div>
          </div>

          <div className="mt-10 border-t border-slate-100 pt-6 space-y-3 text-center text-xs text-slate-400">
            <p className="max-w-3xl mx-auto leading-relaxed text-slate-400/90">
              * Same-day bank transfers are processed via Same Day ACH on banking business days (Monday–Friday, excluding federal holidays). Transfers initiated before 2:00 PM EST on business days typically settle the same day. Transfers initiated after 2:00 PM EST, on weekends, or on federal banking holidays settle on the next business day (e.g. transfers initiated on Friday afternoon or over the weekend settle on Monday). Settlement timing may vary depending on your bank.
            </p>
            <p>© 2026 Oryn Technologies Inc. All rights reserved. Clero is a financial technology platform, not a bank.</p>
          </div>
        </div>
      </footer>
    </PageShell>
  );
};
export default CompareShell;
